import { useEffect, useState, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Bell,
  CheckCircle2,
  XCircle,
  CheckCheck,
  Smartphone,
  BellOff,
  ArrowLeft,
  Database,
  Calendar,
  Clock,
  ListChecks,
} from 'lucide-react'
import clsx from 'clsx'
import { useNotificationsStore, NotificationItem } from '@/store/notifications'
import { biApi } from '@/lib/api'
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  getPushStatus,
} from '@/lib/push'

function formatDateSpanish(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('es-CO', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

interface DetailData {
  id: number
  type: string
  title: string
  body: string
  eventData: any
  createdAt: string
}

function fmtNumber(n: number): string {
  return n.toLocaleString('es-CO')
}

function fmtRate(clicks: number, conversions: number): string {
  return clicks > 0 ? `${((conversions / clicks) * 100).toFixed(2)}%` : '0.00%'
}

function DailySummaryDetail({ data }: { data: any }) {
  if (!data) return null

  const totals = data.totals || { clicks: 0, conversions: 0 }
  const byCountry: any[] = data.byCountry || []
  const byOperator: any[] = data.byOperator || []
  const bySource: any[] = data.bySource || []

  const summaryTable = (rows: any[], keyLabel: string, keyFn: (r: any) => string) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            <th className="py-2 pr-4">{keyLabel}</th>
            <th className="py-2 pr-4 text-right">Clicks</th>
            <th className="py-2 pr-4 text-right">Conversiones</th>
            <th className="py-2 text-right">Tasa</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="py-3 text-gray-400 text-xs">Sin actividad registrada</td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="py-2 pr-4 text-gray-900 dark:text-white">{keyFn(r)}</td>
              <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(r.clicks)}</td>
              <td className="py-2 pr-4 text-right text-green-600 dark:text-green-400 tabular-nums">{fmtNumber(r.conversions)}</td>
              <td className="py-2 text-right text-orange-600 dark:text-orange-400 tabular-nums">{fmtRate(r.clicks, r.conversions)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
            <Database className="w-4 h-4" />
            <span className="text-xs font-medium">Clicks del día</span>
          </div>
          <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">
            {fmtNumber(totals.clicks)}
          </p>
        </div>
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
            <ListChecks className="w-4 h-4" />
            <span className="text-xs font-medium">Conversiones</span>
          </div>
          <p className="text-xl font-bold text-green-600 dark:text-green-400 tabular-nums">
            {fmtNumber(totals.conversions)}
          </p>
        </div>
        <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium">Ventana</span>
          </div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            00:00 — {String(data.windowHours || 18).padStart(2, '0')}:00 (Colombia)
          </p>
        </div>
      </div>

      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Por país</h3>
        {summaryTable(byCountry, 'País', (r) => r.country)}
      </div>

      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Por operador</h3>
        {summaryTable(byOperator, 'Operador', (r) => `${r.country} / ${r.operator}`)}
      </div>

      <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Por fuente de tráfico</h3>
        {summaryTable(bySource, 'Fuente', (r) => r.source)}
      </div>
    </div>
  )
}

export default function Notifications() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { unreadCount, items, lastSuccess, lastFailure, load, markRead, markAllRead } = useNotificationsStore()
  const [selected, setSelected] = useState<DetailData | null>(null)
  const [pushStatus, setPushStatus] = useState<{ supported: boolean; permission: NotificationPermission; subscribed: boolean } | null>(null)
  const [pushBusy, setPushBusy] = useState(false)

  const loadDetail = useCallback(async (id: number) => {
    try {
      const response = await biApi.getNotificationDetail(id)
      if (response.data.success) {
        setSelected(response.data.data)
        markRead(id)
      }
    } catch (err) {
      console.error('Error loading notification detail:', err)
    }
  }, [markRead])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const eventId = searchParams.get('event')
    if (eventId && Number.isFinite(Number(eventId))) {
      loadDetail(Number(eventId))
    }
  }, [searchParams, loadDetail])

  useEffect(() => {
    let cancelled = false
    getPushStatus()
      .then((status) => { if (!cancelled) setPushStatus(status) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [pushBusy])

  const handleTogglePush = async () => {
    setPushBusy(true)
    if (pushStatus?.subscribed) {
      await unsubscribeFromPushNotifications()
    } else {
      await subscribeToPushNotifications()
    }
    const status = await getPushStatus()
    setPushStatus(status)
    setPushBusy(false)
  }

  if (selected) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => { setSelected(null); navigate('/notifications') }}
          className="btn-secondary btn-sm flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a notificaciones
        </button>

        <div className="card p-6">
          <div className="flex items-start gap-4">
            {selected.type === 'backfill_failed' ? (
              <XCircle className="w-8 h-8 text-red-500 shrink-0" />
            ) : (
              <CheckCircle2 className="w-8 h-8 text-green-500 shrink-0" />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {selected.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                {selected.body}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                {formatDateTime(selected.createdAt)}
              </p>
            </div>
          </div>

          {selected.type === 'daily_summary' ? (
            <DailySummaryDetail data={selected.eventData} />
          ) : (
            <>
              {selected.eventData && (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs font-medium">Período cubierto</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {selected.eventData.startDate === selected.eventData.endDate
                    ? formatDateSpanish(selected.eventData.startDate)
                    : `${formatDateSpanish(selected.eventData.startDate)} — ${formatDateSpanish(selected.eventData.endDate)}`}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <Database className="w-4 h-4" />
                  <span className="text-xs font-medium">Filas procesadas</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {selected.eventData.rowsAffected ?? '—'}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <ListChecks className="w-4 h-4" />
                  <span className="text-xs font-medium">Ventana (días)</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {selected.eventData.days ?? '—'}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs font-medium">Origen</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {selected.eventData.triggeredBy === 'manual' ? 'Manual' : 'Cron automático'}
                </p>
              </div>
            </div>
              )}

              {selected.eventData?.error && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">Detalle del error</p>
                  <p className="text-xs text-red-600 dark:text-red-300 mt-1 font-mono">
                    {selected.eventData.error}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Notificaciones
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Estado de las actualizaciones de estadísticas
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="btn-secondary btn-sm flex items-center gap-2"
          >
            <CheckCheck className="w-4 h-4" />
            Marcar todas como leídas
          </button>
        )}
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Última actualización exitosa
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {lastSuccess
                  ? `${formatDateTime(lastSuccess.createdAt)}`
                  : 'Aún no hay actualizaciones registradas'}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <XCircle className={clsx('w-6 h-6', lastFailure ? 'text-red-500' : 'text-gray-300')} />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Último fallo
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {lastFailure
                  ? `${formatDateTime(lastFailure.createdAt)}`
                  : 'Sin fallos registrados'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Push management */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-50 dark:bg-orange-900/30 rounded-lg">
              <Smartphone className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Notificaciones móviles
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {!pushStatus || !pushStatus.supported
                  ? 'No soportadas en este navegador'
                  : pushStatus.permission === 'denied'
                    ? 'Permiso bloqueado en el navegador'
                    : pushStatus.subscribed
                      ? 'Activadas — recibirás las actualizaciones en este dispositivo'
                      : 'Desactivadas'}
              </p>
            </div>
          </div>
          {pushStatus?.supported && pushStatus.permission !== 'denied' && (
            <button
              onClick={handleTogglePush}
              disabled={pushBusy}
              className={clsx(
                'btn-sm flex items-center gap-2',
                pushStatus.subscribed ? 'btn-secondary' : 'btn-primary'
              )}
            >
              {pushStatus.subscribed ? (
                <>
                  <BellOff className="w-4 h-4" />
                  {pushBusy ? 'Desactivando...' : 'Desactivar'}
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  {pushBusy ? 'Activando...' : 'Activar'}
                </>
              )}
            </button>
          )}
        </div>
        {pushStatus?.permission === 'denied' && (
          <p className="text-xs text-gray-400 mt-2">
            Para reactivarlas, habilita las notificaciones de este sitio en la configuración del navegador.
          </p>
        )}
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Historial
          </h3>
        </div>
        {items.length === 0 && (
          <div className="p-10 text-center text-gray-400">
            <Bell className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No hay notificaciones todavía.</p>
            <p className="text-xs mt-1">
              Aparecerán cuando el proceso diario de actualización se ejecute.
            </p>
          </div>
        )}
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {items.map((item: NotificationItem) => (
            <button
              key={item.id}
              onClick={() => loadDetail(item.id)}
              className={clsx(
                'w-full text-left px-4 py-4 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors',
                !item.read && 'bg-orange-50/40 dark:bg-orange-900/10'
              )}
            >
              {item.type === 'backfill_failed' ? (
                <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={clsx(
                    'text-sm font-medium truncate',
                    item.read ? 'text-gray-700 dark:text-gray-300' : 'text-gray-900 dark:text-white'
                  )}>
                    {item.title}
                  </p>
                  {!item.read && <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {item.body}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {formatDateTime(item.createdAt)}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
